import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FingerPrintIcon } from '@heroicons/react/24/outline'
import { answerWithPasskey, wasDismissed, wasRefusedByBrowser } from '../../services/passkeys'
import { AUTH_ERROR, isLoginOver, readAuthError } from '../../services/authErrors'

/**
 * VH-296 — "Use fingerprint or face" (as HubPlatform's HP-39 PasskeyPrompt):
 * asks the server for a challenge, lets the phone answer it and sends the
 * answer. The login's code step and the confirmation before a sensitive
 * action both use it, each with its own calls (`ask`, `send`).
 *
 * A prompt the user closed is said quietly; a refused answer says to try
 * again or use the code, which the caller always offers beside this button.
 */
export default function PasskeyPrompt({ ask, send, onDone, onExpired, idPrefix }) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const errorId = `${idPrefix}-passkey-error`

  const go = async () => {
    setError(null)
    setBusy(true)
    try {
      const { data } = await ask()
      const credential = await answerWithPasskey(data.options)
      const answer = await send(credential)
      onDone(answer.data)
    } catch (err) {
      if (wasDismissed(err)) {
        setError(t('passkey.login.cancelled'))
        return
      }
      // Any other refusal of the browser or the phone (not the server's).
      if (wasRefusedByBrowser(err)) {
        setError(t('passkey.login.refused'))
        return
      }
      if (onExpired && isLoginOver(err)) {
        onExpired()
        return
      }
      const { kind } = readAuthError(err)
      if (kind === AUTH_ERROR.HELD) setError(t('auth.loginRefused'))
      else if (kind === AUTH_ERROR.NETWORK) setError(t('secondFactor.code.unreachable'))
      else if (kind === AUTH_ERROR.NO_PASSKEY) setError(t('passkey.login.none'))
      else setError(t('passkey.login.refused'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={go}
        disabled={busy}
        aria-describedby={error ? errorId : undefined}
        data-testid={`${idPrefix}-passkey`}
        className="btn-primary inline-flex w-full items-center justify-center gap-2 py-3 text-base"
      >
        <FingerPrintIcon className="h-5 w-5" aria-hidden="true" />
        {busy ? t('passkey.login.waiting') : t('passkey.login.use')}
      </button>
      <div aria-live="assertive">
        {error && (
          <p id={errorId} role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
